import { useState } from 'react';
import { CheckCircle2, XCircle, Clock, FileText, MessageSquare } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';

const exceptions = [
  { id: 'EXC-2026-0041', type: 'Policy Override', model: 'GPT-4o Mini', workflow: 'Document Processing', requestedBy: 'doc-team@meridian.global', risk: 'HIGH', approver: 'J. Mueller', due: 'Apr 17', status: 'Pending',
    rationale: 'Requesting temporary policy override to process backlog of 1,247 pending documents while compliance audit for GPT-4o Mini is completed. Current unaudited status is blocking critical operational workflow. Estimated audit completion: Apr 19, 2026.',
    justification: 'Low data sensitivity — documents are internal administrative submissions only. No PII or regulated data involved. Human reviewer confirms each output before action is taken.',
    evidence: ['audit-timeline-gpt4o-mini.pdf', 'document-sensitivity-assessment.pdf'],
    auditTrail: [
      { time: 'Apr 15, 09:14', event: 'Exception submitted', actor: 'doc-team@meridian.global' },
      { time: 'Apr 15, 09:31', event: 'Assigned to J. Mueller', actor: 'system' },
      { time: 'Apr 15, 10:02', event: 'Reviewed by J. Mueller', actor: 'status: Pending additional review' },
      { time: 'Apr 15, 14:00', event: 'Reminder sent', actor: 'system' },
      { time: 'Now', event: 'Awaiting approval decision', actor: '' },
    ],
  },
  { id: 'EXC-2026-0040', type: 'Compliance Gap Extension', model: 'Claude Sonnet', workflow: 'Legal Drafting', requestedBy: 'legal-team@meridian.global', risk: 'HIGH', approver: 'S. Chen', due: 'Apr 16', status: 'Pending',
    rationale: 'Extension request for EU AI Act Art. 13 transparency documentation gap. Current documentation covers 6/9 required controls.',
    justification: 'Documentation team is actively working on remaining 3 controls. Expected completion by May 15. No customer-facing impact during gap period.',
    evidence: ['gap-analysis-ldrs.pdf', 'remediation-timeline.xlsx'],
  },
  { id: 'EXC-2026-0039', type: 'Audit Delay Request', model: 'Mistral 7B', workflow: 'Training Recommendations', requestedBy: 'academy-team@meridian.global', risk: 'MEDIUM', approver: 'K. Patel', due: 'Apr 18', status: 'Pending',
    rationale: 'Request to delay scheduled audit by 2 weeks due to model retraining cycle in progress.',
    justification: 'Current model version will be deprecated. New version audit will cover both versions. No additional risk during delay.',
    evidence: ['retraining-schedule.pdf'],
  },
  { id: 'EXC-2026-0038', type: 'Data Source Exception', model: 'GPT-4o', workflow: 'Classification Pipeline', requestedBy: 'class-team@meridian.global', risk: 'LOW', approver: 'J. Mueller', due: 'Apr 20', status: 'Pending',
    rationale: 'Request to add supplementary external data source for improved classification accuracy.',
    justification: 'Source has been vetted by security team. Data lineage documentation complete. Read-only access.',
    evidence: ['source-vetting-report.pdf', 'data-lineage-map.json'],
  },
  { id: 'EXC-2026-0037', type: 'Policy Override', model: 'Claude Sonnet', workflow: 'Legal Drafting', requestedBy: 'legal-team@meridian.global', risk: 'HIGH', approver: 'S. Chen', due: 'Apr 10', status: 'Approved',
    approvedBy: 'S. Chen', approvedAt: 'Apr 10, 14:22 UTC', comment: 'Approved with condition: weekly monitoring reports required.',
  },
  { id: 'EXC-2026-0036', type: 'Audit Delay Request', model: 'GPT-4o Mini', workflow: 'Document Processing', requestedBy: 'doc-team@meridian.global', risk: 'MEDIUM', approver: 'K. Patel', due: 'Apr 8', status: 'Rejected',
    rejectedBy: 'K. Patel', rejectedAt: 'Apr 8, 09:15 UTC', comment: 'Unaudited model cannot operate past compliance deadline. Audit must proceed as scheduled.',
  },
];

const RiskBadge = ({ risk }) => {
  const cls = risk === 'HIGH' ? 'risk-high' : risk === 'MEDIUM' ? 'risk-medium' : 'risk-low';
  return <span className={`${cls} text-[10px] font-mono px-2 py-0.5 rounded-full`}>{risk}</span>;
};

function ExceptionDetail({ exc, onClose }) {
  if (!exc) return <div className="flex items-center justify-center h-full text-[#475569] text-sm">Select an exception to view details</div>;
  return (
    <ScrollArea className="h-full">
      <div className="p-5 space-y-4">
        <div>
          <div className="font-mono text-sm font-bold text-[#F8FAFC] mb-1">{exc.id}</div>
          <div className="text-xs text-[#64748B]">{exc.type} · {exc.model}</div>
        </div>
        {exc.status === 'Pending' && (
          <>
            <div><div className="text-[10px] text-[#475569] uppercase mb-1">Rationale</div><div className="text-xs text-[#94A3B8] leading-relaxed">{exc.rationale}</div></div>
            <div><div className="text-[10px] text-[#475569] uppercase mb-1">Risk Justification</div><div className="text-xs text-[#94A3B8] leading-relaxed">{exc.justification}</div></div>
            {exc.evidence && (
              <div><div className="text-[10px] text-[#475569] uppercase mb-2">Supporting Evidence</div>
                {exc.evidence.map((f) => (
                  <div key={f} className="flex items-center gap-2 p-2 rounded-lg bg-[#080D1A] border border-[#1E2A45] mb-1.5 text-xs text-[#94A3B8]">
                    <FileText size={12} className="text-[#6366F1]" />{f}
                  </div>
                ))}
              </div>
            )}
            <div className="pt-3 border-t border-[#1E2A45]">
              <textarea placeholder="Add comment..." className="w-full px-3 py-2 bg-[#080D1A] border border-[#1E2A45] rounded-lg text-xs text-[#F8FAFC] placeholder:text-[#475569] focus:outline-none focus:border-[#6366F1] mb-3 h-16 resize-none" />
              <div className="flex gap-2">
                <button data-testid="approve-exception" onClick={() => toast.success(`Exception ${exc.id} approved`)} className="flex-1 flex items-center justify-center gap-2 bg-[#10B981] hover:bg-[#059669] text-white text-xs font-medium py-2.5 rounded-lg transition-all">
                  <CheckCircle2 size={13} /> Approve
                </button>
                <button data-testid="reject-exception" onClick={() => toast.error(`Exception ${exc.id} rejected`)} className="flex-1 flex items-center justify-center gap-2 bg-[#EF4444] hover:bg-[#DC2626] text-white text-xs font-medium py-2.5 rounded-lg transition-all">
                  <XCircle size={13} /> Reject
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <div className="text-[10px] text-[#475569] uppercase">Audit Trail</div>
              {exc.auditTrail ? exc.auditTrail.map((entry, i) => (
                <div key={i} className="flex items-start gap-2">
                  <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${
                    entry.time === 'Now' ? 'bg-[#F59E0B]/10 border border-[#F59E0B]/20' : 'bg-[#10B981]/10 border border-[#10B981]/20'
                  }`}>
                    <Clock size={8} className={entry.time === 'Now' ? 'text-[#F59E0B]' : 'text-[#10B981]'} />
                  </div>
                  <div>
                    <div className="text-[10px] text-[#F8FAFC]">{entry.time} · {entry.event}</div>
                    {entry.actor && <div className="text-[9px] text-[#475569] font-mono">{entry.actor}</div>}
                  </div>
                </div>
              )) : (
                <>
                  <div className="flex items-start gap-2"><div className="w-4 h-4 rounded-full bg-[#3B82F6]/10 border border-[#3B82F6]/20 flex items-center justify-center"><Clock size={8} className="text-[#3B82F6]" /></div><div className="text-[10px] text-[#64748B]">Submitted · {exc.requestedBy}</div></div>
                  <div className="flex items-start gap-2"><div className="w-4 h-4 rounded-full bg-[#F59E0B]/10 border border-[#F59E0B]/20 flex items-center justify-center"><Clock size={8} className="text-[#F59E0B]" /></div><div className="text-[10px] text-[#F59E0B]">Under Review · Assigned to {exc.approver}</div></div>
                  <div className="flex items-start gap-2"><div className="w-4 h-4 rounded-full bg-[#475569]/20 border border-[#475569]/30 flex items-center justify-center"><Clock size={8} className="text-[#475569]" /></div><div className="text-[10px] text-[#475569]">Pending decision</div></div>
                </>
              )}
            </div>
          </>
        )}
        {exc.status === 'Approved' && (
          <div className="space-y-3">
            <div className="p-3 rounded-lg bg-[#10B981]/5 border border-[#10B981]/20">
              <div className="text-xs text-[#10B981] font-medium mb-1">Approved by {exc.approvedBy}</div>
              <div className="text-[10px] text-[#64748B]">{exc.approvedAt}</div>
              <div className="text-xs text-[#94A3B8] mt-2 flex items-start gap-1.5"><MessageSquare size={11} className="mt-0.5 flex-shrink-0" />{exc.comment}</div>
            </div>
          </div>
        )}
        {exc.status === 'Rejected' && (
          <div className="space-y-3">
            <div className="p-3 rounded-lg bg-[#EF4444]/5 border border-[#EF4444]/20">
              <div className="text-xs text-[#EF4444] font-medium mb-1">Rejected by {exc.rejectedBy}</div>
              <div className="text-[10px] text-[#64748B]">{exc.rejectedAt}</div>
              <div className="text-xs text-[#94A3B8] mt-2 flex items-start gap-1.5"><MessageSquare size={11} className="mt-0.5 flex-shrink-0" />{exc.comment}</div>
            </div>
          </div>
        )}
      </div>
    </ScrollArea>
  );
}

export default function ExceptionsApprovals() {
  const [selected, setSelected] = useState(exceptions[0]);
  const pending = exceptions.filter((e) => e.status === 'Pending').length;
  const approved = exceptions.filter((e) => e.status === 'Approved').length;
  const rejected = exceptions.filter((e) => e.status === 'Rejected').length;

  return (
    <div data-testid="exceptions-approvals" className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="glass-card p-4"><div className="text-[10px] text-[#475569] uppercase tracking-wider mb-1">Pending Approval</div><div className="text-2xl font-bold text-[#F59E0B]">{pending}</div></div>
        <div className="glass-card p-4"><div className="text-[10px] text-[#475569] uppercase tracking-wider mb-1">Approved This Month</div><div className="text-2xl font-bold text-[#10B981]">{approved + 12}</div></div>
        <div className="glass-card p-4"><div className="text-[10px] text-[#475569] uppercase tracking-wider mb-1">Rejected</div><div className="text-2xl font-bold text-[#64748B]">{rejected + 3}</div></div>
        <div className="glass-card p-4"><div className="text-[10px] text-[#475569] uppercase tracking-wider mb-1">Avg Resolution Time</div><div className="text-2xl font-bold text-[#F8FAFC]">2.3 <span className="text-sm text-[#64748B]">days</span></div></div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4" style={{ minHeight: 400 }}>
        <div className="lg:col-span-3 glass-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-[#1E2A45]">
                {['ID', 'Type', 'Model', 'Workflow', 'Risk', 'Approver', 'Due', 'Status'].map((h) => (
                  <th key={h} className="text-left text-[10px] font-medium text-[#475569] uppercase tracking-wider py-3 px-3">{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {exceptions.map((exc) => (
                  <tr key={exc.id} data-testid={`exception-row-${exc.id}`} onClick={() => setSelected(exc)} className={`border-b border-[#1E2A45]/50 table-row-interactive ${selected?.id === exc.id ? 'bg-[#6366F1]/5' : ''}`}>
                    <td className="py-2.5 px-3 font-mono text-[#6366F1] text-xs">{exc.id}</td>
                    <td className="py-2.5 px-3 text-[#F8FAFC] text-xs">{exc.type}</td>
                    <td className="py-2.5 px-3 text-[#94A3B8] text-xs">{exc.model}</td>
                    <td className="py-2.5 px-3 text-[#94A3B8] text-xs">{exc.workflow}</td>
                    <td className="py-2.5 px-3"><RiskBadge risk={exc.risk} /></td>
                    <td className="py-2.5 px-3 text-[#94A3B8] text-xs">{exc.approver}</td>
                    <td className="py-2.5 px-3 text-[#64748B] text-xs">{exc.due}</td>
                    <td className="py-2.5 px-3"><span className={`text-[10px] px-2 py-0.5 rounded-full ${exc.status === 'Pending' ? 'bg-[#F59E0B]/10 text-[#F59E0B]' : exc.status === 'Approved' ? 'bg-[#10B981]/10 text-[#10B981]' : 'bg-[#64748B]/10 text-[#64748B]'}`}>{exc.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="lg:col-span-2 glass-card overflow-hidden" data-testid="exception-detail-panel">
          <ExceptionDetail exc={selected} onClose={() => setSelected(null)} />
        </div>
      </div>
    </div>
  );
}
